import { useState, useEffect } from "react";
import { useLanguage } from "../context/LanguageContext";
import { Shield, Settings } from "lucide-react";
import { getLegalPath } from "../lib/site";

const texts = {
  de: {
    title: "Cookie-Einstellungen",
    description: "Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. Einige Cookies sind technisch notwendig, andere helfen uns, unsere Angebote zu verbessern.",
    necessary: "Notwendig",
    necessaryDesc: "Technisch erforderliche Cookies für die Grundfunktionen der Website.",
    analytics: "Analyse",
    analyticsDesc: "Helfen uns zu verstehen, wie Besucher unsere Website nutzen.",
    marketing: "Marketing",
    marketingDesc: "Werden verwendet, um Werbung relevanter zu gestalten.",
    acceptAll: "Alle akzeptieren",
    acceptSelected: "Auswahl bestätigen",
    rejectAll: "Nur notwendige",
    settings: "Einstellungen",
    privacy: "Datenschutzerklärung",
    imprint: "Impressum",
    alwaysOn: "Immer aktiv",
  },
  en: {
    title: "Cookie Settings",
    description: "We use cookies to provide you with the best possible experience on our website. Some cookies are technically necessary, others help us improve our services.",
    necessary: "Necessary",
    necessaryDesc: "Technically required cookies for basic website functionality.",
    analytics: "Analytics",
    analyticsDesc: "Help us understand how visitors use our website.",
    marketing: "Marketing",
    marketingDesc: "Used to make advertising more relevant to you.",
    acceptAll: "Accept All",
    acceptSelected: "Confirm Selection",
    rejectAll: "Necessary Only",
    settings: "Settings",
    privacy: "Privacy Policy",
    imprint: "Imprint",
    alwaysOn: "Always on",
  },
};

export default function CookieBanner() {
  const { language } = useLanguage();
  const t = texts[language] || texts.en;
  const consentKey = "scandicdev-cookie-consent";
  const isPrerender =
    typeof window !== "undefined" && window.__PRERENDER__ === true;

  const [isReady, setIsReady] = useState(false);
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (isPrerender) {
      return undefined;
    }

    try {
      const consent = localStorage.getItem(consentKey);
      setVisible(!consent);
    } catch {
      setVisible(true);
    } finally {
      setIsReady(true);
    }
  }, [consentKey, isPrerender]);

  const saveConsent = (choices) => {
    try {
      localStorage.setItem(consentKey, JSON.stringify({
        necessary: true,
        analytics: choices.analytics,
        marketing: choices.marketing,
        timestamp: new Date().toISOString(),
      }));
    } catch {
      // If storage is unavailable, we still close the banner for the current session.
    }
    setVisible(false);
    setShowSettings(false);
  };

  const handleAcceptAll = () => {
    saveConsent({ analytics: true, marketing: true });
  };

  const handleRejectAll = () => {
    saveConsent({ analytics: false, marketing: false });
  };

  const handleAcceptSelected = () => {
    saveConsent({ analytics, marketing });
  };

  if (isPrerender || !isReady || !visible) return null;

  return (
    <div className="fixed inset-0 z-[9998] flex items-end justify-center p-4 sm:p-6 pointer-events-none" data-testid="cookie-banner-overlay">
      <div
        className="pointer-events-auto w-full max-w-2xl rounded-2xl border border-zinc-700/60 bg-zinc-900/95 backdrop-blur-xl shadow-2xl shadow-black/40 animate-in slide-in-from-bottom-8 duration-500"
        data-testid="cookie-banner"
      >
        <div className="p-5 sm:p-6">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-600/20">
                <Shield className="h-4.5 w-4.5 text-violet-400" />
              </div>
              <h3 className="text-base font-semibold text-white font-['Space_Grotesk']">{t.title}</h3>
            </div>
          </div>

          {/* Description */}
          <p className="text-sm text-zinc-400 leading-relaxed mb-4">{t.description}</p>

          {/* Settings Panel */}
          {showSettings && (
            <div className="mb-4 space-y-2.5" data-testid="cookie-settings">
              {/* Necessary */}
              <div className="flex items-center justify-between rounded-lg bg-zinc-800/60 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-white">{t.necessary}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">{t.necessaryDesc}</p>
                </div>
                <span className="text-xs text-emerald-400 font-medium px-2 py-0.5 rounded bg-emerald-400/10">{t.alwaysOn}</span>
              </div>
              {/* Analytics */}
              <label className="flex items-center justify-between rounded-lg bg-zinc-800/60 px-4 py-3 cursor-pointer group">
                <div>
                  <p className="text-sm font-medium text-white">{t.analytics}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">{t.analyticsDesc}</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={analytics}
                  onClick={() => setAnalytics(!analytics)}
                  className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors ${analytics ? 'bg-violet-600' : 'bg-zinc-600'}`}
                  data-testid="cookie-toggle-analytics"
                >
                  <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform mt-0.5 ${analytics ? 'translate-x-4 ml-0.5' : 'translate-x-0.5'}`} />
                </button>
              </label>
              {/* Marketing */}
              <label className="flex items-center justify-between rounded-lg bg-zinc-800/60 px-4 py-3 cursor-pointer group">
                <div>
                  <p className="text-sm font-medium text-white">{t.marketing}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">{t.marketingDesc}</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={marketing}
                  onClick={() => setMarketing(!marketing)}
                  className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors ${marketing ? 'bg-violet-600' : 'bg-zinc-600'}`}
                  data-testid="cookie-toggle-marketing"
                >
                  <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform mt-0.5 ${marketing ? 'translate-x-4 ml-0.5' : 'translate-x-0.5'}`} />
                </button>
              </label>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-2">
            {showSettings ? (
              <button
                onClick={handleAcceptSelected}
                className="flex-1 rounded-full bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium py-2.5 px-4 transition-colors"
                data-testid="cookie-accept-selected"
              >
                {t.acceptSelected}
              </button>
            ) : (
              <button
                onClick={handleAcceptAll}
                className="flex-1 rounded-full bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium py-2.5 px-4 transition-colors"
                data-testid="cookie-accept-all"
              >
                {t.acceptAll}
              </button>
            )}
            <button
              onClick={handleRejectAll}
              className="flex-1 rounded-full border border-zinc-700 hover:border-zinc-500 text-zinc-300 hover:text-white text-sm font-medium py-2.5 px-4 transition-colors"
              data-testid="cookie-reject-all"
            >
              {t.rejectAll}
            </button>
            {!showSettings && (
              <button
                onClick={() => setShowSettings(true)}
                className="flex items-center justify-center gap-1.5 rounded-full border border-zinc-700 hover:border-zinc-500 text-zinc-400 hover:text-white text-sm font-medium py-2.5 px-4 transition-colors"
                data-testid="cookie-show-settings"
              >
                <Settings className="w-3.5 h-3.5" />
                {t.settings}
              </button>
            )}
          </div>

          {/* Footer links */}
          <div className="flex items-center justify-center gap-3 mt-3 text-xs text-zinc-600">
            <a href={getLegalPath("datenschutz", language)} className="hover:text-zinc-400 transition-colors">{t.privacy}</a>
            <span>|</span>
            <a href={getLegalPath("impressum", language)} className="hover:text-zinc-400 transition-colors">{t.imprint}</a>
          </div>
        </div>
      </div>
    </div>
  );
}
